import {
  LeaderboardQueryParams,
  LeaderboardTrader,
  MarketActivity,
  MarketClosedPosition,
} from '@/types';
import axiosClient from './axiosClient';

import { API_ENDPOINTS } from '@/constants';

export interface LeaderboardResponse {
  success: boolean;
  data: LeaderboardTrader[];
}

export interface LeaderboardCategoriesResponse {
  success: boolean;
  data: string[];
}

export interface LeaderboardTimePeriodsResponse {
  success: boolean;
  data: string[];
}

export const leaderboardApi = {
  /**
   * Get trader leaderboard based on PNL or Volume
   */
  getLeaderboard: async (params?: LeaderboardQueryParams): Promise<LeaderboardTrader[]> => {
    const response = await axiosClient.get<LeaderboardResponse>(API_ENDPOINTS.LEADERBOARD.BASE, {
      params,
    });
    return response.data?.data || [];
  },

  /**
   * Get available categories for the leaderboard
   */
  getCategories: async (): Promise<string[]> => {
    const response = await axiosClient.get<LeaderboardCategoriesResponse>(
      API_ENDPOINTS.LEADERBOARD.CATEGORIES,
    );
    return response.data?.data || [];
  },

  /**
   * Get available time periods for the leaderboard
   */
  getTimePeriods: async (): Promise<string[]> => {
    const response = await axiosClient.get<LeaderboardTimePeriodsResponse>(
      API_ENDPOINTS.LEADERBOARD.TIME_PERIODS,
    );
    return response.data?.data || [];
  },

  /**
   * Get the biggest winners from Polymarket based on profit
   */
  getBiggestWinners: async (params?: LeaderboardQueryParams): Promise<LeaderboardTrader[]> => {
    const response = await axiosClient.get<LeaderboardResponse>(
      API_ENDPOINTS.LEADERBOARD.BIGGEST_WINNERS,
      {
        params,
      },
    );
    return response.data?.data || [];
  },

  /**
   * Get the monetary value of a specific user
   */
  getUserValue: async (user: string): Promise<number | null> => {
    const response = await axiosClient.get<{
      success: boolean;
      data: { user: string; value: number }[];
    }>(API_ENDPOINTS.LEADERBOARD.USER_VALUE, { params: { user } });
    return response.data?.data?.[0]?.value ?? null;
  },

  /**
   * Get user stats from markets/stats endpoint
   */
  getUserStats: async (user: string) => {
    const response = await axiosClient.get<{ success: boolean; data: any }>(
      API_ENDPOINTS.MARKETS_STATS(user),
    );
    return response.data?.data ?? null;
  },

  /**
   * Get public profile data of a specific user with positions and activity
   */
  getPublicProfile: async (address: string, params?: any) => {
    const response = await axiosClient.get<{ success: boolean; data: any }>(
      API_ENDPOINTS.LEADERBOARD.PUBLIC_PROFILE,
      { params: { address, ...params } },
    );
    return response.data?.data ?? null;
  },
  /**
   * Get user activity from markets/activity endpoint
   */
  getUserActivity: async (user: string, limit = 10, offset = 0): Promise<MarketActivity[]> => {
    const response = await axiosClient.get<{ success: boolean; data: MarketActivity[] }>(
      API_ENDPOINTS.MARKETS_ACTIVITY_PORTFOLIO,
      { params: { user, limit, offset } },
    );
    return response.data?.data || [];
  },
  /**
   * Get sorting categories for active or closed positions
   */
  getSortCategories: async (type: 'active-positions' | 'closed-positions'): Promise<any[]> => {
    const response = await axiosClient.get(API_ENDPOINTS.LEADERBOARD.SORT_CATEGORIES(type));
    if (response.data?.success && response.data?.data) {
      return response.data.data;
    }
    return [];
  },
  /**
   * Get user positions
   */
  getUserPositions: async (params: {
    user: string;
    title?: string;
    sizeThreshold?: number;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortDirection?: string;
  }): Promise<any[]> => {
    const response = await axiosClient.get(API_ENDPOINTS.MARKETS_POSITIONS, {
      params,
    });
    return response.data?.data || [];
  },
  /**
   * Get user closed positions
   */
  getUserClosedPositions: async (params: {
    user: string;
    title?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortDirection?: string;
  }): Promise<MarketClosedPosition[]> => {
    const response = await axiosClient.get<{ success: boolean; data: MarketClosedPosition[] }>(
      API_ENDPOINTS.MARKETS_CLOSED_POSITIONS,
      { params },
    );
    return response.data?.data || [];
  },
};
